<template>
  <div class="max-w-7xl mx-auto px-4 py-8 font-inter">
    <!-- Header, tagline and mode switch-->
    <div class="mb-6">
      <div class="flex justify-between mb-6">
        <div class="flex flex-col">
          <h1 class="text-3xl font-bold text-gray-900">Talent Communities</h1>
          <span class="text-gray-500">Discover and apply to talent pools that match your skills and interests</span>
        </div>
        
        <div class="flex items-center justify-center">
          <div class="flex bg-[#f5f5f5] rounded-lg p-1">
            <button
              @click="viewMode = 'grid'"
              :class="[
                'px-4 py-2 rounded-md text-sm font-medium transition-all duration-200',
                viewMode === 'grid' 
                  ? 'bg-newPrimary text-white shadow-sm' 
                  : 'text-[#737373] hover:text-newPrimary'
              ]"
            >
              <span class="flex items-center gap-2">
                <Grid3x3 class="h-4 w-4" />
                Grid
              </span>
            </button>

            <button
              @click="viewMode = 'map'"
              :class="[
                'px-4 py-2 rounded-md text-sm font-medium transition-all duration-200',
                viewMode === 'map' 
                ? 'bg-newPrimary text-white shadow-sm' 
                : 'text-[#737373] hover:text-newPrimary'
              ]"
            >
              <span class="flex items-center gap-2">
                <Map class="h-4 w-4" />
                Map
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Stats bar -->
    <StatsBar :stats="contractorStats" :is-loading="talentPoolsStore.isLoading" />

    <!-- Filter bar -->
    <FilterBar @filters-changed="updateFilters" class="mb-6" />

    <div v-if="talentPoolsStore.isLoading" class="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-3 gap-6">
      <CardSkeleton v-for="i in 6" :key="i" />
    </div>

    <div v-else-if="filteredTalentPools.length > 0" class="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-3 gap-6">
      <Card v-for="pool in filteredTalentPools" :key="pool.id" :talentPool="pool" />
    </div>


    

    <div v-else class="text-center py-16">
      <div class="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
        <svg class="w-12 h-12 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
        </svg>
      </div>
      <h3 class="text-xl font-medium text-gray-900 mb-2">No Talent Communities Found</h3>
      <p class="text-gray-500 max-w-md mx-auto">
        There are currently no open talent pools matching your search. Please check back soon for new Talent Communities.
      </p>
    </div>
  </div>
</template>

<script setup lang="js">
// ===================================
// 1. IMPORTS
// ===================================
import { ref, onMounted, computed } from 'vue'
import { Button } from '@/components/marketplace/ui'
import { StatsBar, FilterBar, Card, CardSkeleton } from '@/components/marketplace/talentPools/contractor'
import { useTalentPoolsStore } from '@/stores/talentPoolsStore'
import { Map, Grid3x3 } from 'lucide-vue-next'

// ===================================
// 2. PROPS & EMITS
// ===================================

// ===================================
// 3. STATE
// ===================================
const talentPoolsStore = useTalentPoolsStore();
const viewMode = ref('grid')

const activeFilters = ref({});

const selectedTalentPool = ref(null)


const contractorStats = computed(() => {
  const pools = talentPoolsStore.talentPools;

  // Calculate each stat based on the pools array
  const totalOpportunities = pools.length;
  const applicationsSent = pools.filter(p => p.applicationStatus).length;
  const accepted = pools.filter(p => p.applicationStatus === 'Accepted').length;
  // Combine 'Applied' and 'Under Review' for a general "Pending" count
  const pending = pools.filter(p => ['Applied', 'Under Review'].includes(p.applicationStatus)).length;

  // Return the array in the required format
  return [
    { label: 'Total Talent Communities', value: totalOpportunities, color: 'newPrimary' },
    { label: 'Applications Sent', value: applicationsSent, color: 'newPrimary' },
    { label: 'Accepted', value: accepted, color: 'newPrimary' },
    { label: 'Pending', value: pending, color: 'newPrimary' },
  ];
});

const filteredTalentPools = computed(() => {
  // Start with the full, original list from the store
  let pools = talentPoolsStore.talentPools;

  // 1. Filter by Search Query
  const query = activeFilters.value.query;
  if (query) {
    const lowerQuery = query.toLowerCase();
    pools = pools.filter(pool => 
      // Check if the title includes the query
      pool.title.toLowerCase().includes(lowerQuery) ||
      // Check if any of the skills/qualifications include the query
      pool.qualifications.some(skill => skill.toLowerCase().includes(lowerQuery)) ||
      // Check if the company name includes the query
      (pool.company_name && pool.company_name.toLowerCase().includes(lowerQuery))
    );
  }

  // 2. Filter by Work Type
  const type = activeFilters.value.type;
  if (type) { // This will be null for "All Types"
    pools = pools.filter(pool => pool.work_type === type);
  }

  // 3. Filter by Application Status
  const status = activeFilters.value.status;
  if (status) { // This will be null for "All Talent Communities"
    if (status === 'my-applications') {
      // Show only pools where the user has an application
      pools = pools.filter(pool => pool.applicationStatus);
    } else if (status === 'available') {
      // Show only pools where the user has NOT applied
      pools = pools.filter(pool => !pool.applicationStatus);
    }
  }

  // 4. Filter by Company Industry
  const industryId = activeFilters.value.industry;
  if (industryId !== null && industryId !== undefined && industryId !== '') {
    const industryNumber = Number(industryId);
    pools = pools.filter(pool => Number(pool.company_industry_id) === industryNumber);
  }
  
  // Return the final, filtered array
  return pools;
});


// ===================================
// 4. COMPUTED PROPERTIES
// ===================================

// ===================================
// 5. WATCHERS
// ===================================

// ===================================
// 6. LIFECYCLE HOOKS
// ===================================
onMounted(() => {
  talentPoolsStore.fetchTalentPools('contractor')
})
// ===================================
// 7. FUNCTIONS
// ===================================
const updateFilters = (filtersFromChild) => {
  console.log('Filters received from child:', filtersFromChild);
  activeFilters.value = filtersFromChild;
};
</script>
